// deps
import React, { ReactElement } from "react";
import classNames from "classnames";

import { CardBrandDetails } from "@swiggy-private/payment-types";
import { isNonEmptyString } from "@swiggy-private/payment-core";
import { format, getCloudinaryUrl } from "@swiggy-private/core-ui";

// components
import DetailsContent, { DetailsRenderProps } from "./DetailsContent";
import InputField from "./Input";

// widgets
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";

// test-ids
import { ADD_NEW_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

//helpers
import { getCardIcon } from "@payment-ui/helpers/Card";

import styles from "../AddNewCard.v4.scss";
import { TEXTS } from "@payment-ui/constants";

export interface DefaultDetailsLayoutProps {
    details: {
        cardNumber: string;
        cvv: string;
        name: string;
        expiry: string;
        nickName: string;
    };
    errors: {
        cardNumber: boolean;
        cvv: boolean;
        expiry: boolean;
        name: boolean;
        nickName: boolean;
    };
    updateDetails: (data: { cardNumber: string; cvv: string; name: string; expiry: string; nickName: string }) => void;
    resetError: (data: string) => void;
    cardBrandDetails: CardBrandDetails;
    cardNumberErrorMessage?: string;
    onCardNumberChange: (cardBin: string) => void;
    onCardNumberBlur?: (cardNumber: string) => void;
    rewardApplicable?: boolean;
    payableAmount?: string;
}

function DefaultDetailsLayout(props: DefaultDetailsLayoutProps): ReactElement {
    const { rewardApplicable = true } = props || {};
    return (
        <DetailsContent {...props}>
            {({
                cardBrand,
                details,
                onInputChange,
                errors,
                cardInputs,
                onCardNumberInputBlur,
            }: DetailsRenderProps): ReactElement => {
                const logoClass = classNames(styles.newCardDetailsLogo, {
                    [styles.newCardDetailsLogoShow]: cardBrand,
                    [styles.newCardDetailsLogoUx4]: true,
                });

                const nickNameClass = classNames(styles.newCardRowInputContainer);

                const classObj = {
                    [styles.newCardDetailsLogoImg]: true,
                };

                //Fetching Icon Details from Static Data
                const cardIcon = getCardIcon(cardBrand);

                if (cardBrand) {
                    const padding = cardIcon?.padding || 1;
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    const style = styles[`newCardDetailsLogoImgWithPadding${padding}`];
                    classObj[style] = true;
                }

                const imgClass = classNames(classObj);

                const icon = !cardIcon ? null : (
                    <div className={logoClass}>
                        <PaymentMethodIcon
                            src={getCloudinaryUrl(cardIcon.id, {
                                trim: true,
                            })}
                            imgClassName={imgClass}
                            parentClassName={styles.newCardDetailsLogoParent}
                            data-testid={ADD_NEW_CARD_TEST_IDS.CARD_ICON}
                        />
                    </div>
                );

                const showErrorForCardNumber = props.cardNumberErrorMessage != null || errors.cardNumber;

                const warningLabel = format(TEXTS.WARNING_INFO_ADD_CARD, props?.payableAmount ?? "0");

                return (
                    <div className={styles.newCardDetails}>
                        <div className={styles.newCardDetailsRow}>
                            <InputField
                                value={details.cardNumber}
                                onChange={onInputChange}
                                showError={showErrorForCardNumber}
                                {...cardInputs.cardNumber}
                                errorLabel={props.cardNumberErrorMessage || cardInputs.cardNumber.errorLabel}
                                keepLabelActive={isNonEmptyString(details.cardNumber)}
                                onBlur={onCardNumberInputBlur}
                                warningLabel={!rewardApplicable ? warningLabel : ""}
                                showWarning={!rewardApplicable}
                            />
                            {showErrorForCardNumber ? null : icon}
                        </div>

                        <div className={styles.newCardInlineContainer}>
                            <div className={styles.newCardInlineInputContainer}>
                                <InputField
                                    value={details.expiry}
                                    onChange={onInputChange}
                                    showError={errors.expiry}
                                    {...{
                                        ...cardInputs.expiry,
                                        label: cardInputs.expiry.labelV4,
                                    }}
                                    keepLabelActive={isNonEmptyString(details.expiry)}
                                />
                            </div>

                            <div className={styles.newCardInlineInputContainer}>
                                <InputField
                                    value={details.cvv}
                                    onChange={onInputChange}
                                    showError={errors.cvv}
                                    {...cardInputs.cvv}
                                    keepLabelActive={isNonEmptyString(details.cvv)}
                                />
                            </div>
                        </div>

                        <InputField
                            value={details.name}
                            onChange={onInputChange}
                            showError={errors.name}
                            {...cardInputs.name}
                            keepLabelActive={isNonEmptyString(details.name)}
                        />

                        <div className={nickNameClass}>
                            <InputField
                                value={details.nickName}
                                onChange={onInputChange}
                                showError={errors.nickName}
                                {...cardInputs.nickName}
                                keepLabelActive={isNonEmptyString(details.nickName)}
                            />
                        </div>
                    </div>
                );
            }}
        </DetailsContent>
    );
}

export default React.memo(DefaultDetailsLayout);
